import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { MILES_NEARYBY_LESSONS } from 'constants/seo';
import { SportsEnum } from 'types/generated/client';
import { GetCityCoachesQuery } from 'types/generated/server';
import { getAllCityVenuePaths } from 'services/server/graphql/queries/getAllCityVenuePaths';
import { getCityCoaches } from 'services/server/graphql/queries/getCityCoaches';
import { getCityTennisVenuesByCitySlug } from 'services/server/graphql/queries/getCityVenuesByCitySlug';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import ExploreLessons from 'screens/ExploreLessons';
import { LessonPageModeEnum } from 'screens/ExploreLessons/enums';
import CityLocationProvider from 'components/utilities/CityLocationProvider';

const REVALIDATE_IN_SECONDS = 6 * 60 * 60 * 1; // 6 hours
const IS_ALL_AS_ISR = true;
const SPORT = SportsEnum.Tennis;

export interface Props {
  citySlug: string;
  cityData:
    | NonNullable<Awaited<ReturnType<typeof getCityTennisVenuesByCitySlug>>>['cities'][0]
    | null;
  coaches: GetCityCoachesQuery['userProfiles'];
}

export const getStaticProps: GetStaticProps<Props, { citySlug: string }> = async ({ params }) => {
  const { citySlug } = params || {};

  if (!citySlug) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    // Fetch city data using existing query pattern
    const cityVenueResponse = await getCityTennisVenuesByCitySlug({ slug: citySlug });
    const cityData = cityVenueResponse?.cities[0];

    if (!cityData || !cityData?.id || !cityData?.name) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    // Fetch coaches for this city and sport using radius-based search
    const sport = SportsEnum.Tennis;
    const searchRadius = milesToMeters(MILES_NEARYBY_LESSONS);
    const coachesResponse = await getCityCoaches({
      distance: searchRadius,
      from: {
        type: 'Point',
        coordinates: [cityData.longitude, cityData.latitude],
      },
      sport,
      defaultSport: sport as string,
      limit: 1000,
    });

    const coaches = coachesResponse?.userProfiles || [];

    // Check if we have coaches for this sport in this city
    if (coaches.length === 0) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    return {
      props: {
        citySlug,
        cityData,
        coaches,
      },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (IS_ALL_AS_ISR) {
    return {
      paths: [],
      fallback: true,
    };
  }

  try {
    const citiesData = await getAllCityVenuePaths();
    const paths = [];

    for (const city of citiesData.cities) {
      // Only include cities that have venues (which indicates active cities)
      if (city.venues.length > 0) {
        paths.push({
          params: {
            citySlug: city.slug,
          },
        });
      }
    }

    return {
      paths,
      fallback: true,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      paths: [],
      fallback: true,
    };
  }
};

const TennisCityLessonsPage = (props: Props) => {
  if (!props.cityData) {
    return null; // This will be handled by Next.js fallback
  }

  return (
    <CityLocationProvider
      city={{
        ...props.cityData,
        state: props.cityData.countrySubdivision?.name,
        country: props.cityData.countrySubdivision?.country?.id,
      }}
    >
      <ExploreLessons
        pageMode={LessonPageModeEnum.EXPLORE}
        initialSport={SPORT}
        cityData={props.cityData}
        coaches={props.coaches}
        title={`Tennis Lessons Near Me in ${props.cityData.name}${
          props.cityData.countrySubdivision?.name
            ? `, ${props.cityData.countrySubdivision?.name}`
            : ''
        }`}
        subtitle={`Find the best tennis coach near me in ${props.cityData.name}`}
      />
    </CityLocationProvider>
  );
};

export default TennisCityLessonsPage;
